import React, { useMemo } from 'react';
import { Card, Typography, Box, Stack, CircularProgress, Alert } from '@mui/material';
import { styled } from '@mui/material/styles';
import { Icon } from '@iconify/react';
import environment from '@iconify/icons-ant-design/environment';
import checkCircle from '@iconify/icons-ant-design/check-circle';
import thunderbolt from '@iconify/icons-ant-design/thunderbolt';
import { BaseChart } from 'src/components/charts/BaseOptionChart';
import { isSameDay } from 'date-fns';

// ----------------------------------------------------------------------

const StyledCard = styled(Card)(({ theme }) => ({
  padding: theme.spacing(3),
  borderRadius: theme.shape.borderRadiusMd,
  boxShadow: theme.customShadows.z4,
  border: `1px solid ${theme.palette.success.light}40`,
  background: `linear-gradient(135deg, ${theme.palette.success.lighter}08 0%, ${theme.palette.success.light}05 100%)`,
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
}));

const MetricCard = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2.5),
  borderRadius: theme.shape.borderRadius * 1.5,
  background: `linear-gradient(135deg, ${theme.palette.background.paper} 0%, ${theme.palette.success.lighter}15 50%, ${theme.palette.success.light}08 100%)`,
  border: `1px solid ${theme.palette.success.light}30`,
  textAlign: 'center',
  height: '100%',
  minHeight: '120px',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  position: 'relative',
  overflow: 'hidden',
  transition: 'all 0.3s ease',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: `0 8px 25px ${theme.palette.success.light}20`,
    border: `1px solid ${theme.palette.success.light}50`,
  },
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: '3px',
    background: `linear-gradient(90deg, ${theme.palette.success.main} 0%, ${theme.palette.success.light} 100%)`,
    borderRadius: `${theme.shape.borderRadius * 1.5}px ${theme.shape.borderRadius * 1.5}px 0 0`,
  },
}));

// Process environmental data from transactions
const processEnvironmentalData = (apiData, timeInterval) => {
  if (!apiData || !Array.isArray(apiData) || !timeInterval) {
    return { 
      data: [
        {
          name: 'CO₂ Emissions (kg)',
          type: 'line',
          data: [0],
          color: '#E53E3E'
        },
        {
          name: 'Trees Saved',
          type: 'line',
          data: [0],
          color: '#38A169'
        },
        {
          name: 'Carbon Offset (kg)',
          type: 'line',
          data: [0],
          color: '#3182CE'
        }
      ], 
      labels: ['No Data'] 
    };
  }

  const { start, end } = timeInterval;
  const startDate = new Date(start);
  const endDate = new Date(end);
  const days = Math.ceil((endDate - startDate) / (1000 * 60 * 60 * 24)) + 1;
  
  // Initialize arrays for each day
  const co2Emissions = new Array(days).fill(0);
  const treesSaved = new Array(days).fill(0);
  const carbonOffset = new Array(days).fill(0);
  const labels = [];
  const labelDates = [];

  // Process each day
  for (let i = 0; i < days; i++) {
    const currentDate = new Date(startDate);
    currentDate.setDate(startDate.getDate() + i);
    labelDates.push(new Date(currentDate));
    labels.push(currentDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }));

    // Find transactions for this day
    const dayTransactions = apiData.filter(transaction => {
      const transactionDate = new Date(transaction.start_time);
      return transactionDate.toDateString() === currentDate.toDateString();
    });

    // Calculate environmental metrics for this day
    let dayEnergy = 0;
    let dayCO2 = 0;
    let dayTrees = 0;
    let dayOffset = 0;

    dayTransactions.forEach(transaction => {
      const energy = parseFloat(transaction.energy_consumed) || 0;
      dayEnergy += energy;
      
      // CO2 emission factors (kg CO2 per kWh)
      const GRID_EMISSION_FACTOR = 0.5; // Average grid electricity emission factor
      const RENEWABLE_EMISSION_FACTOR = 0.05; // Renewable energy emission factor
      
      // Calculate baseline CO2 emissions (if all energy was from grid)
      const baselineCO2 = energy * GRID_EMISSION_FACTOR;
      
      // Calculate actual CO2 emissions (assuming some renewable energy mix)
      const renewablePercentage = 0.2; // 20% renewable
      const gridEnergy = energy * (1 - renewablePercentage);
      const renewableEnergy = energy * renewablePercentage;
      
      const actualCO2 = (gridEnergy * GRID_EMISSION_FACTOR) + (renewableEnergy * RENEWABLE_EMISSION_FACTOR);
      
      dayCO2 += actualCO2;
      dayTrees += Math.round(actualCO2 / 22); // 1 tree absorbs ~22kg CO2 per year
      dayOffset += baselineCO2 - actualCO2;
    });

    co2Emissions[i] = Math.round(dayCO2);
    treesSaved[i] = dayTrees;
    carbonOffset[i] = Math.round(dayOffset);
  }

  // Remove only the last date if it's in the future (not today)
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  
  let filteredLabels = labels;
  let filteredCo2Emissions = co2Emissions;
  let filteredTreesSaved = treesSaved;
  let filteredCarbonOffset = carbonOffset;

  // Check only the last date
  if (labelDates.length > 0) {
    const lastDate = new Date(labelDates[labelDates.length - 1]);
    lastDate.setHours(0, 0, 0, 0);
    
    // Remove last date only if it's in the future (strictly greater than today)
    if (lastDate > today) {
      filteredLabels = labels.slice(0, -1);
      filteredCo2Emissions = co2Emissions.slice(0, -1);
      filteredTreesSaved = treesSaved.slice(0, -1);
      filteredCarbonOffset = carbonOffset.slice(0, -1);
    }
  }

  return {
    data: [
      {
        name: 'CO₂ Emissions (kg)',
        type: 'line',
        data: filteredCo2Emissions,
        color: '#E53E3E'
      },
      {
        name: 'Trees Saved',
        type: 'line',
        data: filteredTreesSaved,
        color: '#38A169'
      },
      {
        name: 'Carbon Offset (kg)',
        type: 'line',
        data: filteredCarbonOffset,
        color: '#3182CE'
      }
    ],
    labels: filteredLabels
  };
};

// Calculate environmental metrics
const calculateEnvironmentalMetrics = (apiData) => {
  if (!apiData || !Array.isArray(apiData)) {
    return {
      totalCO2Emissions: 0,
      baselineCO2Emissions: 0,
      carbonFootprintReduction: 0,
      sustainabilityScore: 0,
      treesEquivalent: 0,
      carbonOffset: 0,
      renewablePercentage: 0,
      totalEnergy: 0,
      avgDailyCO2: 0,
      avgDailyTrees: 0,
      avgDailyOffset: 0
    };
  }

  let totalEnergy = 0;
  let totalCO2 = 0;
  let totalTrees = 0;
  let totalOffset = 0;

  // CO2 emission factors
  const GRID_EMISSION_FACTOR = 0.5;
  const RENEWABLE_EMISSION_FACTOR = 0.05;
  const renewablePercentage = 0.2;

  apiData.forEach(transaction => {
    const energy = parseFloat(transaction.energy_consumed) || 0;
    totalEnergy += energy;
    
    // Calculate baseline CO2 emissions (if all energy was from grid)
    const baselineCO2 = energy * GRID_EMISSION_FACTOR;
    
    // Calculate actual CO2 emissions
    const gridEnergy = energy * (1 - renewablePercentage);
    const renewableEnergy = energy * renewablePercentage;
    
    const actualCO2 = (gridEnergy * GRID_EMISSION_FACTOR) + (renewableEnergy * RENEWABLE_EMISSION_FACTOR);
    
    totalCO2 += actualCO2;
    totalTrees += Math.round(actualCO2 / 22);
    totalOffset += baselineCO2 - actualCO2;
  });

  const baselineCO2 = totalEnergy * GRID_EMISSION_FACTOR;
  const carbonReduction = baselineCO2 > 0 ? ((baselineCO2 - totalCO2) / baselineCO2) * 100 : 0;
  const sustainabilityScore = Math.min(10, Math.max(1, 5 + (carbonReduction / 10)));

  // Calculate daily averages
  const days = Math.ceil((new Date() - new Date(apiData[0]?.start_time || new Date())) / (1000 * 60 * 60 * 24)) + 1;
  const avgDailyCO2 = days > 0 ? totalCO2 / days : 0;
  const avgDailyTrees = days > 0 ? totalTrees / days : 0;
  const avgDailyOffset = days > 0 ? totalOffset / days : 0;

  return {
    totalCO2Emissions: Math.round(totalCO2),
    baselineCO2Emissions: Math.round(baselineCO2),
    carbonFootprintReduction: Math.round(carbonReduction * 10) / 10,
    sustainabilityScore: Math.round(sustainabilityScore * 10) / 10,
    treesEquivalent: totalTrees,
    carbonOffset: Math.round(totalOffset),
    renewablePercentage: Math.round(renewablePercentage * 100),
    totalEnergy: Math.round(totalEnergy * 10) / 10,
    avgDailyCO2: Math.round(avgDailyCO2),
    avgDailyTrees: Math.round(avgDailyTrees),
    avgDailyOffset: Math.round(avgDailyOffset)
  };
};

// ----------------------------------------------------------------------

export default function EnvironmentalImpact({ timeInterval, transactionsData, isLoading }) {
  const chartData = useMemo(() => {
    if (!transactionsData || !timeInterval || transactionsData.length === 0) return { data: [], labels: [] };
    return processEnvironmentalData(transactionsData, timeInterval);
  }, [transactionsData, timeInterval]);

  const metrics = useMemo(() => {
    if (!transactionsData) return calculateEnvironmentalMetrics([]);
    return calculateEnvironmentalMetrics(transactionsData);
  }, [transactionsData]);

  const chartOptions = useMemo(() => ({
    chart: {
      type: 'line',
      height: 350,
      toolbar: { show: false },
      zoom: { enabled: false },
      pan: { enabled: false },
      selection: { enabled: false },
      brush: { enabled: false },
      animations: { enabled: true },
      fontFamily: 'inherit',
    },
    colors: ['#E53E3E', '#38A169', '#3182CE'],
    stroke: {
      curve: 'smooth',
      width: 3,
    },
    grid: {
      show: true,
      borderColor: 'rgba(0,0,0,0.1)',
      strokeDashArray: 3,
      xaxis: { lines: { show: true } },
      yaxis: { lines: { show: true } },
      padding: { top: 0, right: 0, bottom: 0, left: 0 },
    },
    xaxis: {
      categories: chartData.labels,
      axisBorder: { show: false },
      axisTicks: { show: false },
      labels: {
        style: { fontSize: '12px', colors: '#666' },
        rotate: -45,
      },
    },
    yaxis: {
      labels: {
        style: { fontSize: '12px', colors: '#666' },
        formatter: (value) => Math.round(value).toLocaleString(),
      },
    },
    tooltip: {
      shared: true,
      intersect: false,
      y: {
        formatter: (value) => Math.round(value).toLocaleString(),
      },
      followCursor: true,
      offsetY: -10,
      offsetX: 0,
      fixed: {
        enabled: false
      },
      position: 'top',
      custom: function({ series, seriesIndex, dataPointIndex, w }) {
        const data = w.globals.initialSeries[seriesIndex].data[dataPointIndex];
        const value = data ? Math.round(data).toLocaleString() : '0';
        const seriesName = w.globals.initialSeries[seriesIndex].name || 'Environmental Impact';
        return `
          <div style="
            background: rgba(0, 0, 0, 0.8);
            color: white;
            padding: 8px 12px;
            border-radius: 6px;
            font-size: 12px;
            font-family: Inter, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
            box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
            max-width: 200px;
            word-wrap: break-word;
            z-index: 1000;
          ">
            <div style="font-weight: 600; margin-bottom: 4px;">${seriesName}</div>
            <div>${value}</div>
          </div>
        `;
      }
    },
    legend: {
      show: true,
      position: 'top',
      horizontalAlign: 'left',
      fontSize: '14px',
      fontWeight: 600,
      offsetY: 0,
      itemMargin: { horizontal: 12, vertical: 8 },
      onItemClick: { toggleDataSeries: true },
      onItemHover: { highlightDataSeries: true },
    },
    dataLabels: { enabled: false },
    fill: {
      type: 'gradient',
      gradient: {
        shade: 'light',
        type: 'vertical',
        shadeIntensity: 0.25,
        gradientToColors: ['#E53E3E20', '#38A16920', '#3182CE20'],
        inverseColors: false,
        opacityFrom: 0.8,
        opacityTo: 0.1,
        stops: [0, 100],
      },
    },
    plotOptions: {
      line: {
        dataLabels: { enabled: false },
      },
    },
  }), [chartData.labels]);

  if (isLoading) {
    return (
      <StyledCard>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 400 }}>
          <CircularProgress />
        </Box>
      </StyledCard>
    );
  }


  return (
    <StyledCard>
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: 'text.primary' }}>
            Environmental Impact
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            Track CO₂ emissions, carbon offset, and environmental sustainability
          </Typography>

          {/* Key Metrics - 2 Rows */}
          <Box sx={{ mb: 3 }}>
            {/* First Row */}
            <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
              <MetricCard sx={{ flex: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 1 }}>
                  <Icon icon={thunderbolt} width={20} height={20} style={{ marginRight: 8, color: '#E53E3E' }} />
                  <Typography variant="h6" sx={{ fontWeight: 700, color: 'primary.main' }}>
                    {metrics.totalCO2Emissions.toLocaleString()} kg
                  </Typography>
                </Box>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  Total CO₂ Emissions
                </Typography>
              </MetricCard>

              <MetricCard sx={{ flex: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 1 }}>
                  <Icon icon={checkCircle} width={20} height={20} style={{ marginRight: 8, color: '#38A169' }} />
                  <Typography variant="h6" sx={{ fontWeight: 700, color: 'primary.main' }}>
                    {metrics.treesEquivalent.toLocaleString()}
                  </Typography>
                </Box>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  Trees Equivalent
                </Typography>
              </MetricCard>
            </Stack>

            {/* Second Row */}
            <Stack direction="row" spacing={2}>
              <MetricCard sx={{ flex: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 1 }}>
                  <Icon icon={environment} width={20} height={20} style={{ marginRight: 8, color: '#3182CE' }} />
                  <Typography variant="h6" sx={{ fontWeight: 700, color: 'primary.main' }}>
                    {metrics.carbonOffset.toLocaleString()} kg
                  </Typography>
                </Box>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  Carbon Offset
                </Typography>
              </MetricCard>

              <MetricCard sx={{ flex: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 1 }}>
                  <Icon icon={thunderbolt} width={20} height={20} style={{ marginRight: 8, color: '#805AD5' }} />
                  <Typography variant="h6" sx={{ fontWeight: 700, color: 'primary.main' }}>
                    {metrics.sustainabilityScore}/10
                  </Typography>
                </Box>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  Sustainability Score
                </Typography>
              </MetricCard>
            </Stack>
          </Box>

          {/* Additional Environmental Metrics */}
          <Box sx={{ mb: 3, p: 2, bgcolor: 'success.lighter', borderRadius: 2, border: '1px solid', borderColor: 'success.light' }}>
            <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
              <Icon icon={environment} width={24} height={24} style={{ color: '#38A169' }} />
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'success.dark' }}>
                  Environmental Impact Summary
                </Typography>
                <Typography variant="body2" sx={{ color: 'success.main' }}>
                  Carbon footprint reduction: {metrics.carbonFootprintReduction}%
                </Typography>
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                  Baseline (grid only): {metrics.baselineCO2Emissions.toLocaleString()} kg CO₂
                </Typography>
              </Box>
            </Stack>
            
            {/* Additional Metrics */}
            <Stack direction="row" spacing={2}>
              <Box sx={{ flex: 1, textAlign: 'center', p: 1, bgcolor: 'background.paper', borderRadius: 1 }}>
                <Typography variant="h6" sx={{ fontWeight: 700, color: 'success.main' }}>
                  {metrics.avgDailyCO2}
                </Typography>
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                  Avg Daily CO₂ (kg)
                </Typography>
              </Box>
              <Box sx={{ flex: 1, textAlign: 'center', p: 1, bgcolor: 'background.paper', borderRadius: 1 }}>
                <Typography variant="h6" sx={{ fontWeight: 700, color: 'success.main' }}>
                  {metrics.avgDailyTrees}
                </Typography>
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                  Avg Daily Trees
                </Typography>
              </Box>
              <Box sx={{ flex: 1, textAlign: 'center', p: 1, bgcolor: 'background.paper', borderRadius: 1 }}>
                <Typography variant="h6" sx={{ fontWeight: 700, color: 'success.main' }}>
                  {metrics.avgDailyOffset}
                </Typography>
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                  Avg Daily Offset (kg)
                </Typography>
              </Box>
            </Stack>
          </Box>
        </Box>

        {/* Chart */}
        <Box sx={{ 
          flex: 1, 
          p: { xs: 1, sm: 2 }, 
          pb: { xs: 2, sm: 3 },
          overflow: 'hidden',
          width: '100%'
        }}>
          <Box sx={{ 
            width: '100%', 
            height: { xs: 250, sm: 300, md: 350 },
            overflow: 'hidden',
            position: 'relative'
          }}>
            <BaseChart
              key={`environmental-chart-${timeInterval?.start}-${timeInterval?.end}`}
              type="line"
              series={chartData.data}
              options={chartOptions}
              height="100%"
              width="100%"
            />
          </Box>
        </Box>
      </Box>
    </StyledCard>
  );
}
